import { InfinityIcon } from "@/assets/images/svgIcons/infinityIcon"
import { Button } from "@/components/ui/baseUi/button"
import { successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text, TextWithTooltip } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { useCurrentBoxId } from "@/modules/core/store/useCurrentSpaceManagerStore"
import { cn } from "@/modules/lib/utils"
import { OnOffIcon } from "@images/svgIcons/onOffIcon"
import { ProLargeIcon } from "@images/svgIcons/proLargeIcon"
import { word } from "@modules/core/utils/i18n"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { useMySpaceNavigate } from "@modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@modules/navigation/routes"
import { useProgramRights } from "@modules/programs/hooks/useProgramRights"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { ProgramStatus, ProgramType, ProgramVisibility } from "@modules/programs/types/programTypes"
import { getDisplayVisibility, getSubscriptionCountText } from "@modules/programs/utils/programUtils"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { Dot, GlobeIcon, LockIcon, MessageCircleMore, Share } from "lucide-react"
import { useEffect } from "react"

type ProgramInfoHeaderProps = {
  programId: string
  className?: string
} & React.ComponentProps<"div">

export function ProgramInfoHeader({ programId, className, ...props }: ProgramInfoHeaderProps) {
  const { isAuthor, isAcceptedSharedCoach } = useProgramRights(programId)
  const { data: program } = useProgram(programId)
  const { setCustomHeaderButtons } = useSidebar()
  const navigate = useMySpaceNavigate()
  const boxNavigate = useBoxNavigate()
  const boxId = useCurrentBoxId()

  useEffect(() => {
    const customHeaderIcons = []

    const canUserChatWithMembers =
      program && program.type !== ProgramType.ONOFF && (isAuthor || isAcceptedSharedCoach || program.subscribed)

    if (canUserChatWithMembers) {
      const openChatWithProgramGroup = () => {
        if (programId) {
          if (boxId) {
            boxNavigate(Path.Chat.Root, { state: { programToChat: programId } })
          } else {
            navigate(Path.Chat.Root, { state: { programToChat: programId } })
          }
        }
      }

      customHeaderIcons.push(
        <Tooltip>
          <TooltipTrigger
            render={
              <Button variant="outline" size="iconMd" onClick={openChatWithProgramGroup}>
                <MessageCircleMore color="currentColor" size={16} />
              </Button>
            }
          />
          <TooltipContent side="bottom">
            <Text fontStyle="LM" color="currentColor" className="text-textDefault-body">
              {word("chat.button.goToProgramChat")}
            </Text>
          </TooltipContent>
        </Tooltip>,
      )
    }

    const canUserShareInvitationLink =
      program &&
      program.invitationLink &&
      (program.type === ProgramType.VIRTUAL || program.status === ProgramStatus.PUBLISHED) &&
      (program.visibility === ProgramVisibility.PUBLIC ||
        (program.visibility === ProgramVisibility.PRIVATE && (isAuthor || isAcceptedSharedCoach)))

    if (canUserShareInvitationLink && program.invitationLink) {
      customHeaderIcons.push(
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="outline"
                size="iconMd"
                onClick={() => {
                  if (program.invitationLink) {
                    navigator.clipboard.writeText(program.invitationLink)
                    toast.success(word("program.details.share.success"), {
                      classNames: successToastClassName(),
                    })
                  }
                }}
              >
                <Share color="currentColor" size={16} />
              </Button>
            }
          />
          <TooltipContent side="bottom">
            <Text fontStyle="LM" color="currentColor" className="text-textDefault-body">
              {word("sidebar.button.dynamicLink")}
            </Text>
          </TooltipContent>
        </Tooltip>,
      )
    }

    if (customHeaderIcons.length > 0) {
      setCustomHeaderButtons(customHeaderIcons)
    }
  }, [isAuthor, isAcceptedSharedCoach, program.invitationLink, program.visibility, program.subscribed])

  return (
    <div className={cn("flex h-60 w-full overflow-hidden rounded-b-4xl", className)} {...props}>
      {/* LFET SIDE TEXT */}
      <div className="mx-2 mb-7 flex w-5/10 flex-col items-center justify-center pt-12">
        {program.type === ProgramType.VIRTUAL ? (
          <InfinityIcon color="currentColor" width={24} height={24} className="mb-2 shrink-0 text-textDefault-body" />
        ) : program.type === ProgramType.PRO ? (
          <ProLargeIcon color="currentColor" width={24} height={24} className="mb-2 shrink-0 text-textDefault-body" />
        ) : (
          <OnOffIcon color="currentColor" width={24} height={24} className="mb-2 shrink-0 text-textDefault-body" />
        )}
        <div className="mb-1 flex w-full min-w-0 justify-center">
          <TextWithTooltip
            fontStyle="HS"
            color="currentColor"
            className="line-clamp-3 text-center wrap-break-word"
            title={program.name}
            tooltipContentProps={{ side: "bottom", className: "max-w-60 wrap-anywhere" }}
          >
            {program.name}
          </TextWithTooltip>
        </div>

        <div className="flex flex-row items-center justify-center">
          {program.visibility === ProgramVisibility.PUBLIC ? (
            <GlobeIcon color="currentColor" size={15} />
          ) : (
            <LockIcon color="currentColor" size={15} />
          )}
          <Text color="currentColor" fontStyle="BM" className="ml-1 w-fit text-textDefault-body">
            {`${word("program.details.visibility", {
              visibility: getDisplayVisibility(program.visibility),
            })}
                  `}
          </Text>
          <Dot color="currentColor" className="shrink-0" strokeWidth={5} size={15} />
          <Text fontStyle="BS" color="currentColor" className="text-textDefault-body">
            {getSubscriptionCountText(
              program.subscriptionCount,
              isAuthor,
              isAcceptedSharedCoach,
              true,
              program.type,
            ).toLowerCase()}
          </Text>
        </div>
      </div>
      {/* RIGHT SIDE IMAGE */}
      {program.image ? (
        <div
          className="h-full w-5/10"
          style={{ borderBottomRightRadius: "1rem", clipPath: "polygon(32% 0%, 100% 0%, 100% 100%, 0% 100%)" }}
        >
          <img src={program.image.url} alt="program image" className="h-full w-full object-cover" />
        </div>
      ) : null}
    </div>
  )
}
